import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ImageGallery from "react-image-gallery";
import "react-image-gallery/styles/css/image-gallery.css";
import PhotoViewer from "./PhotoViewer";
import { galleryPhotos, mosaicTileCount } from "../../helper/gallery";

const COMPACT_QUERY = "(max-width: 767px)";

const matchesCompact = () =>
  typeof window.matchMedia === "function" &&
  window.matchMedia(COMPACT_QUERY).matches;

// Phones get one swipeable photo instead of the mosaic.
const useCompactLayout = () => {
  const [compact, setCompact] = useState(matchesCompact);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;
    const query = window.matchMedia(COMPACT_QUERY);
    const update = () => setCompact(query.matches);
    update();
    query.addEventListener("change", update);

    return () => query.removeEventListener("change", update);
  }, []);

  return compact;
};

const GridIcon = () => (
  <svg viewBox='0 0 16 16' width='16' height='16' aria-hidden='true'>
    {[2, 7, 12].flatMap((y) =>
      [2, 7, 12].map((x) => <rect key={`${x}-${y}`} x={x - 1} y={y - 1} width='3' height='3' rx='0.5' />)
    )}
  </svg>
);

// Photos of a property: one large photo with up to four beside it, a button
// for all of them and a viewer that opens on the clicked photo.
const PropertyPhotoMosaic = ({ images = [], featuredImage, tenant, title }) => {
  const { t } = useTranslation();
  const photos = useMemo(
    () => galleryPhotos(images, tenant, featuredImage),
    [images, tenant, featuredImage]
  );
  const compact = useCompactLayout();
  const [viewerIndex, setViewerIndex] = useState(null);
  const [slide, setSlide] = useState(0);
  const rootRef = useRef(null);

  const viewable = !photos[0].placeholder;
  const several = photos.length > 1;
  const openViewer = (index) => {
    if (viewable) setViewerIndex(index);
  };
  const photoLabel = (index) =>
    t("photo_of", {
      current: index + 1,
      total: photos.length,
      defaultValue: "Photo {{current}} of {{total}}",
    });

  const showAll = several && (
    <button
      type='button'
      className='photo-mosaic__show-all'
      onClick={() => openViewer(0)}
    >
      <GridIcon />
      {t("show_all_photos", {
        count: photos.length,
        defaultValue: "Show all photos ({{count}})",
      })}
    </button>
  );

  return (
    <div ref={rootRef} className='photo-mosaic-wrap'>
      {compact ? (
        <div className='photo-mosaic photo-mosaic--compact'>
          <ImageGallery
            items={photos.map((photo, index) => ({
              original: photo.thumb,
              originalAlt: photoLabel(index),
            }))}
            lazyLoad
            showNav={false}
            showThumbnails={false}
            showBullets={false}
            showPlayButton={false}
            showFullscreenButton={false}
            onSlide={setSlide}
            onClick={() => openViewer(slide)}
          />
          {several && (
            <span className='photo-mosaic__counter' aria-label={photoLabel(slide)}>
              {slide + 1} / {photos.length}
            </span>
          )}
        </div>
      ) : (
        <div
          className={`photo-mosaic photo-mosaic--${mosaicTileCount(photos.length)}`}
        >
          {photos.slice(0, mosaicTileCount(photos.length)).map((photo, index) => (
            <button
              key={`${index}-${photo.thumb}`}
              type='button'
              className='photo-mosaic__tile'
              onClick={() => openViewer(index)}
              disabled={!viewable}
              aria-label={photoLabel(index)}
            >
              <img
                src={index === 0 ? photo.full : photo.thumb}
                alt=''
                loading={index === 0 ? "eager" : "lazy"}
              />
            </button>
          ))}
          {showAll}
        </div>
      )}
      {viewerIndex !== null && (
        <PhotoViewer
          photos={photos}
          startIndex={viewerIndex}
          title={title}
          anchorRef={rootRef}
          onClose={() => setViewerIndex(null)}
        />
      )}
    </div>
  );
};

export default PropertyPhotoMosaic;
